import { permisosPrueba, COPY_PA3 } from "../lib/pruebaGratis";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { faltaDeAcceso } from "../lib/acceso";
import SinAcceso from "../components/SinAcceso";
import { loadFacebookSdk, CONFIG_ID } from "../lib/facebookSdk";
import { useAuth } from "../contexts/AuthContext";
import { cargarPlanes, tieneSofi, useDatosAlta } from "../lib/planes";
import { enlaceSoporte } from "../lib/soporte";

import PageHeader from "../components/PageHeader";
import DoctorAvailabilityEditor from "../components/DoctorAvailabilityEditor";
import AusenciasDoctor from "../components/AusenciasDoctor";
import PhoneInput, { phoneDigitsValid } from "../components/PhoneInput";
import { toast } from "sonner";
import {
  Save, Bot, Settings, BookOpen, Phone,
  Clock, AlertOctagon, Plus, Trash2, Edit2, X,
  UserRound,
  Globe, Loader2, AlertTriangle, RefreshCw, CheckCircle2, Mail
} from "lucide-react";

// 🔴 CX fase 0-b -- EL BOTÓN DE EMBEDDED SIGNUP SE ABRE POR CLÍNICA, Y LO DECIDE EL BACKEND.
//    Sustituye a la constante global de TEN.4 (`false` para todas): ahora es
//    `waStatus.embedded_signup_abierto`, que `GET /api/whatsapp/status` calcula con la lista
//    EMBEDDED_SIGNUP_CLINICAS (hoy sólo clinic-pruebas). Cualquier otra clínica sigue con la
//    conexión ACOMPAÑADA de TEN.4, y el backend además rechaza el POST (403
//    EMBEDDED_SIGNUP_CERRADO): esconder no es permiso (DT.71). Sin la bandera tampoco se precarga
//    el SDK de Meta.

// Desenlaces del canal (A) que significan "el flujo de Meta TERMINÓ" (nunca "cancelaste").
// `FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING` es el de Coexistence (doc de Meta).
const DESENLACES_TERMINADOS = new Set([
  "FINISH",
  "FINISH_ONLY_WABA",
  "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
]);

// PASO 4 -- que hacer con cada uno de los 11 codigos de POST /api/whatsapp/connect.
//
// 🔴 Deliberadamente NO se duplica aqui el TEXTO del backend. Los mensajes de `detail` ya vienen
// redactados para una persona (_WHATSAPP_CONNECT_MESSAGES en server_pg.py) y copiarlos crearia dos
// fuentes de verdad que se desincronizan -- es lo que ya paso con DOCTOR_PLAN_LIMITS, duplicado
// entre backend y Doctors.js. Lo que aporta la UI y el backend no puede aportar es la ACCION
// siguiente: reintentar, contactar soporte, o releer el estado.
const WA_ACCION_POR_CODIGO = {
  // Los dos que mas importan, y los unicos con texto propio anadido:
  PHONE_NUMBER_ALREADY_CONNECTED: {
    accion: "soporte",
    extra: "Ese numero ya esta dado de alta en otra cuenta. Escribenos y lo revisamos contigo.",
  },
  AGENCY_TIMEOUT: {
    accion: "reintentar",
    // 🔴 El backend ya avisa de revisar si el numero quedo conectado, y no es cortesia: en un
    // timeout el code de Meta SI pudo consumirse del otro lado. Por eso, ademas, tras este error
    // se recarga el estado automaticamente (ver manejarRespuestaLogin).
    extra: "Ya volvimos a consultar el estado; si aparece conectado arriba, no hace falta repetir.",
  },
  AMBIGUOUS_PHONE_NUMBER: { accion: "soporte" },
  PHONE_DISCOVERY_FAILED: { accion: "reintentar" },
  NO_PHONE_NUMBER: { accion: "soporte" },
  TOKEN_EXCHANGE_FAILED: { accion: "reintentar" },
  INVALID_BODY: { accion: "reintentar" },
  VERTICAL_HAS_NO_OPERATOR: { accion: "soporte" },
  VERTICAL_OPERATOR_AMBIGUOUS: { accion: "soporte" },
  AGENCY_UNAVAILABLE: { accion: "reintentar" },
  NO_ACTIVE_CLINIC: { accion: "sesion" },
  // Fallback del backend cuando agency devuelve algo sin `code`.
  AGENCY_ERROR: { accion: "reintentar" },
};

const TABS = [
  { id: "config", label: "Configuración", icon: Settings },
  { id: "knowledge", label: "Conocimiento", icon: BookOpen },
  { id: "channels", label: "Canales", icon: Globe },
  { id: "escalations", label: "Urgencias", icon: AlertOctagon },
];
const DAY_NAMES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const TONES = [
  { id: "profesional", label: "Profesional", desc: "Lenguaje claro y directo." },
  { id: "calido", label: "Cálido", desc: "Empático y cercano." },
  { id: "formal", label: "Formal", desc: "Tono formal y respetuoso." },
];
const DOC_COLORS = ["#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

// Agrupa filas de doctor_availability (el horario real, S3.1/S3.2) en un texto legible.
// Los días con exactamente las mismas franjas se agrupan juntos, ej.:
// "Lun, Mar, Mié, Jue, Vie · 10:00-14:00 y 16:00-19:00"
function formatAvailability(rows) {
  if (!rows || rows.length === 0) return "Sin horario configurado";
  const byDay = {};
  rows.forEach((r) => {
    const franja = `${r.start_time}-${r.end_time}`;
    (byDay[r.day_of_week] = byDay[r.day_of_week] || []).push(franja);
  });
  const groups = {};
  Object.keys(byDay).forEach((day) => {
    const franjas = byDay[day].slice().sort();
    const sig = franjas.join("|");
    (groups[sig] = groups[sig] || { days: [], franjas }).days.push(Number(day));
  });
  return Object.values(groups)
    .map((g) => `${g.days.sort((a, b) => a - b).map((d) => DAY_NAMES[d]).join(", ")} · ${g.franjas.join(" y ")}`)
    .join("; ");
}
const AssistantConfig = forwardRef(function AssistantConfig({ wizardPaso, onSaved }, ref) {
  const [cfg, setCfg] = useState(null);
  const [cfgError, setCfgError] = useState(null);
  const [sinAcceso, setSinAcceso] = useState(null);
  const [doctoresError, setDoctoresError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(wizardPaso === "whatsapp" ? "channels" : "config");
  const [doctors, setDoctors] = useState([]);
  const [editDoc, setEditDoc] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);
  const [escalations, setEscalations] = useState([]);
  const [loadingEscalations, setLoadingEscalations] = useState(false);

  // ── WhatsApp / Embedded Signup (F1-4a-2) ──
  const [waStatus, setWaStatus] = useState(null);        // los 8 campos de GET /whatsapp/status
  const [waStatusError, setWaStatusError] = useState(null);  // {code, detail} -- ESTADO 4
  const [waLoading, setWaLoading] = useState(false);
  const [waConnecting, setWaConnecting] = useState(false);
  const [waError, setWaError] = useState(null);          // {code, detail, accion, extra}
  const [waAviso, setWaAviso] = useState(null);          // abandono / error reportado por Meta
  // Estado de la PRECARGA del SDK: "cargando" | "listo" | "error". Gobierna si el boton se puede
  // pulsar -- ver conectarWhatsapp y la rama del ESTADO 3.
  const [waSdk, setWaSdk] = useState({ estado: "cargando", error: null });
  // 🔴 REF y no estado: lo escribe el listener de `message`, que llega ANTES que el callback de
  // FB.login y desde fuera del ciclo de React. Con useState, el callback leeria el valor viejo por
  // captura de closure -- y justo ahi vive el waba_id sin el que el POST no se puede hacer.
  const signupRef = useRef(null);
  // CX fase 0-b: la bandera por clínica llega con el estado (ver el comentario de arriba).
  const { user: usuarioPrueba, refrescarUsuario } = useAuth();
  const permisosPA3 = permisosPrueba(usuarioPrueba);
  const esperaPA3 = permisosPA3?.puede_conectar === false;
  const signupAbierto = waStatus?.embedded_signup_abierto === true && !esperaPA3;
  // WEB.CL3 (DT.269): los enlaces de SOPORTE van al correo de soporte, leído de
  // /api/publico/alta (TEN.4: nunca escrito aquí). Sin correo, no se pinta el enlace.
  const correoSoporte = useDatosAlta().datos?.contacto_email;

  const clinicSlug = (() => {
    try { return JSON.parse(localStorage.getItem("surit_user") || "{}").active_clinic_id || "mi-clinica"; }
    catch { return "mi-clinica"; }
  })();

  useEffect(() => {
    api.get("/assistant/config")
      .then((r) => {
        const d = r.data || {};
        setCfg({
          assistant_name: d.assistant_name || "Sofi",
          welcome_message: d.welcome_message || "Hola, ¿en qué te puedo ayudar?",
          business_hours: d.business_hours || "L-V 9:00-19:00",
          handoff_phone: d.handoff_phone || "",
          emergency_notice: d.emergency_notice || "",
          instrucciones_pago: d.instrucciones_pago || "",
          services_offered: Array.isArray(d.services_offered) ? d.services_offered : [],
          faqs: Array.isArray(d.faqs) ? d.faqs : [],
          tone: d.tone || "profesional",
          auto_replies: d.auto_replies !== undefined ? d.auto_replies : true,
          human_approval: d.human_approval !== undefined ? d.human_approval : false,
          // PASO 1 (J1, DT.61): system_prompt deliberadamente NO entra al estado del formulario
          // -- ver nota junto a "Contacto y horarios" abajo.
          is_active: d.is_active !== undefined ? d.is_active : true,
        });
      })
      // 🔴 COL.12 -- un 403 por capacidad NO es «no se pudo cargar». Esta pantalla es la
      //    que lo destapó: un colaborador sin `configuracion` que llegaba aquí (escribiendo
      //    la URL, o con la ruta guardada) veía un error rojo de avería y reportaba que el
      //    panel estaba roto. El servidor estaba haciendo lo suyo.
      //    El guardia de ruta (`ExigeCapacidad`) corta antes en el caso normal; esto es la
      //    red de seguridad para cuando la SESIÓN dice que sí y el SERVIDOR dice que no
      //    -- una capacidad revocada mientras la pestaña seguía abierta.
      .catch((e) => {
        const acceso = faltaDeAcceso(e);
        setSinAcceso(acceso);
        if (!acceso) setCfgError("No se pudo cargar la configuración del asistente");
      });
    // ⚠️ `/doctors` exige `agenda`, NO `configuracion`: esta pantalla mezcla DOS
    //    capacidades, así que alguien con `configuracion` y sin `agenda` entra bien y
    //    recibe 403 sólo aquí. Por eso su fallo NO puede tapar la pantalla entera -- pero
    //    tampoco desaparecer en silencio, que es lo que hacía este `catch` vacío: la lista
    //    de médicos quedaba vacía y eso se lee como «esta clínica no tiene médicos».
    if (!wizardPaso) api.get("/doctors")
      .then(r => setDoctors(r.data.items || []))
      .catch((e) => setDoctoresError(faltaDeAcceso(e)
        ? "No ves el catálogo de médicos porque tu acceso no incluye la agenda."
        : "No se pudo cargar el catálogo de médicos."));
  }, [wizardPaso]);

  // PASO 3 -- se relee de la FUENTE, nunca se deduce del POST.
  const cargarWhatsappStatus = useCallback(async () => {
    setWaLoading(true);
    setWaStatusError(null);
    try {
      const { data } = await api.get("/whatsapp/status");
      setWaStatus(data);
    } catch (e) {
      // 🔴 El error NO se colapsa en `null` ni en un objeto vacio, a diferencia del catch de
      // escalations. "No pude leer el estado" y "no esta conectada" son cosas distintas: si se
      // confundieran, el tab ofreceria conectar un WhatsApp que quiza ya lo esta. Es ESTADO 4.
      setWaStatus(null);
      const codigo = e?.response?.data?.code;
      const esDemo = e?.response?.status === 403;
      setWaStatusError({
        code: codigo || (esDemo ? "DEMO" : "DESCONOCIDO"),
        detail: esDemo
          ? "La demo pública no puede conectar WhatsApp."
          : formatApiError(e?.response?.data?.detail),
      });
    } finally {
      setWaLoading(false);
    }
  }, []);

  // 🔴 loadFacebookSdk se precarga al ENTRAR al tab, no en el onClick. Un await entre el gesto de
  // usuario y FB.login consume el gesto y el navegador bloquea el popup. Solo se nota en el primer
  // clic: en el segundo el SDK ya esta en memoria, asi que el bug es intermitente y desaparece al
  // depurarlo.
  //
  // Sigue siendo carga BAJO DEMANDA -- ni en public/index.html ni al arrancar la aplicacion. El
  // panel entero no depende de connect.facebook.net; solo este tab.
  const precargarSdk = useCallback(() => {
    setWaSdk({ estado: "cargando", error: null });
    loadFacebookSdk()
      .then(() => setWaSdk({ estado: "listo", error: null }))
      // Si la precarga falla, el tab cae al estado de error del SDK CON reintento. Nunca se deja
      // un boton activo sobre un SDK que no cargo: pulsarlo no podria hacer nada.
      .catch((e) => setWaSdk({ estado: "error", error: e.message }));
  }, []);

  // Mismo patron que el useEffect de escalations: el tab pide lo suyo al activarse.
  useEffect(() => {
    if (activeTab !== "channels" || esperaPA3) return;
    cargarWhatsappStatus();
  }, [activeTab, cargarWhatsappStatus, esperaPA3]);

  // La precarga del SDK depende de la bandera, que llega CON el estado: se lanza en cuanto el
  // backend dice que esta clínica tiene el Embedded Signup abierto, antes de cualquier clic.
  useEffect(() => {
    if (activeTab !== "channels" || !signupAbierto) return;
    precargarSdk();
  }, [activeTab, signupAbierto, precargarSdk]);

  // TEN.4: ¿el plan de la clínica incluye WhatsApp? Lo dice `limits.sofi` del catálogo de agency
  // -- ninguna lista de planes en este repo. `sofi: null` = el plan no está en el catálogo (los
  // planes antiguos): se trata como con WhatsApp, porque esas clínicas lo tenían contratado.
  const { activeClinic } = useAuth();
  const [planWa, setPlanWa] = useState({ estado: "cargando", sofi: null });
  useEffect(() => {
    if (activeTab !== "channels") return;
    let vivo = true;
    cargarPlanes()
      .then((lista) => {
        const plan = lista.find((x) => x.slug === activeClinic?.plan_slug);
        if (vivo) setPlanWa({ estado: "ok", sofi: plan ? tieneSofi(plan) : null });
      })
      .catch(() => vivo && setPlanWa({ estado: "error", sofi: null }));
    return () => { vivo = false; };
  }, [activeTab, activeClinic?.plan_slug]);

  // ── CANAL (A) del Embedded Signup: el postMessage de Meta ──
  // Trae waba_id / phone_number_id / business_id y el desenlace, pero NO trae el code. Se registra
  // solo mientras el tab esta activo; no tiene sentido escuchar a Meta desde la Agenda.
  useEffect(() => {
    if (activeTab !== "channels") return;
    const alRecibirMensaje = (event) => {
      // 🔴 Filtro de origen por HOSTNAME, no por `event.origin.endsWith("facebook.com")`. Esa
      // comprobacion de cadena la pasaria `https://notfacebook.com`, que es un dominio que
      // cualquiera puede registrar. Se compara el host exacto o un subdominio real.
      let host;
      try { host = new URL(event.origin).hostname; } catch { return; }
      if (host !== "facebook.com" && !host.endsWith(".facebook.com")) return;

      let datos;
      try {
        datos = JSON.parse(event.data);   // Meta manda una cadena JSON
      } catch {
        return;   // otros postMessage del propio Facebook mandan objetos: no son para nosotros
      }
      if (datos?.type !== "WA_EMBEDDED_SIGNUP") return;

      // 🔴 phone_number_id PUEDE VENIR AUSENTE (alta de WABA sin numero elegido). No se exige:
      // el backend lo acepta opcional y agency lo descubre por su cuenta.
      signupRef.current = {
        waba_id: datos?.data?.waba_id,
        phone_number_id: datos?.data?.phone_number_id,
        business_id: datos?.data?.business_id,
        event: datos?.event,
        current_step: datos?.data?.current_step,
        error_message: datos?.data?.error_message,
        session_id: datos?.data?.session_id || datos?.session_id,
      };
    };
    window.addEventListener("message", alRecibirMensaje);
    return () => window.removeEventListener("message", alRecibirMensaje);
  }, [activeTab]);

  // ── CANAL (B): el callback de FB.login, el unico que trae el code ──
  //
  // 🔴 El SDK de Meta NO acepta promesas en su superficie de callbacks. Ni `await` ANTES de
  // FB.login (consume el gesto de usuario y el navegador bloquea el popup), ni `async` DENTRO del
  // callback (FB.login valida el tipo y lanza "Expression is of type asyncfunction, not
  // function"). Wrapper sincrono + logica async por dentro.
  //
  // Esta es la logica async. NO se le pasa a FB.login: se le pasa `alResponderLogin`, mas abajo.
  const procesarRespuestaLogin = async (response) => {
    const datos = signupRef.current;
    const code = response?.authResponse?.code;

    if (!code) {
      // Sin code no hay nada que mandar. Los desenlaces se distinguen por el canal (A).
      // CX fase 0-b: un desenlace TERMINADO (FINISH, o el de Coexistence) nunca se lee como
      // cancelación. Sin code no hubo alta de nuestro lado, pero se relee el estado de la fuente
      // por si acaso. El texto es el genérico que ya existía (uno nuevo pasa por Jesús).
      if (datos?.event && DESENLACES_TERMINADOS.has(datos.event)) {
        await cargarWhatsappStatus();
        setWaAviso({ tono: "aviso", texto: "La conexión no se completó. Vuelve a intentarlo.", pie: null });
      } else if (datos?.event === "CANCEL" && datos?.error_message) {
        setWaAviso({
          tono: "error",
          texto: `Meta reportó un problema: ${datos.error_message}`,
          pie: datos.session_id ? `Referencia para soporte: ${datos.session_id}` : null,
        });
      } else if (datos?.event === "CANCEL") {
        setWaAviso({
          tono: "aviso",
          texto: datos?.current_step
            ? `Cerraste la conexión en el paso "${datos.current_step}". Puedes volver a empezar cuando quieras.`
            : "Cerraste la conexión antes de terminar. Puedes volver a empezar cuando quieras.",
          pie: null,
        });
      } else {
        setWaAviso({ tono: "aviso", texto: "La conexión no se completó. Vuelve a intentarlo.", pie: null });
      }
      setWaConnecting(false);
      return;
    }

    // 🔴 HAY code: esto es un alta, aunque el canal (A) no haya dicho FINISH. En la pantalla final
    // cerrar el popup con la X cuenta como EXITO, igual que pulsar Finish -- salir ahi NO es
    // cancelar. Si la UI tratara el cierre como cancelacion, le diria "cancelaste" a alguien que
    // acaba de dar de alta su numero correctamente.
    //
    // CX fase 0-b: si llegó (B) sin (A) ya NO se corta aquí. `waba_id` es opcional en el backend y
    // agency la descubre con el token canjeado; cortar aquí tiraba un `code` que sí servía. Se manda lo
    // que llegó, sin inventar nada: lo ausente va como null.
    try {
      await api.post("/whatsapp/connect", {
        code,
        waba_id: datos?.waba_id ?? null,
        phone_number_id: datos?.phone_number_id ?? null,
        business_id: datos?.business_id ?? null,
      });
      toast.success("WhatsApp conectado");
      if (permisosPA3) await refrescarUsuario().catch(() => {});
      // 🔴 No se pinta "conectado" con la respuesta del POST: se relee de la fuente.
      await cargarWhatsappStatus();
    } catch (e) {
      // El `code` va TOP-LEVEL desde el bloque F1-4a (DT.60 refinado).
      const codigo = e?.response?.data?.code || "AGENCY_ERROR";
      const cfgCodigo = WA_ACCION_POR_CODIGO[codigo] || { accion: "reintentar" };
      setWaError({
        code: codigo,
        detail: formatApiError(e?.response?.data?.detail),
        accion: cfgCodigo.accion,
        extra: cfgCodigo.extra,
      });
      // 🔴 Tras un timeout, el code pudo haberse consumido del otro lado: se relee el estado para
      // que el usuario vea si en realidad quedo conectado, en vez de reintentar a ciegas.
      if (codigo === "AGENCY_TIMEOUT") await cargarWhatsappStatus();
    } finally {
      setWaConnecting(false);
    }
  };

  // 🔴 EL WRAPPER QUE RECIBE FB.LOGIN. Sincrono, y no devuelve la promesa: si retornara
  // `procesarRespuestaLogin(response)` volveria a ser una funcion que devuelve una promesa, que es
  // justo lo que el SDK rechaza. Se llama y se descarta el valor.
  //
  // 🔴 Y por eso hace falta el `.catch`: nadie espera esa promesa, asi que un throw dentro de
  // `procesarRespuestaLogin` se perderia como unhandled rejection -- sin error en pantalla y, peor,
  // sin limpiar `waConnecting`, dejando el boton en "Conectando..." para siempre.
  const alResponderLogin = (response) => {
    procesarRespuestaLogin(response).catch((e) => {
      setWaError({
        code: "ERROR_INESPERADO",
        detail: "No se pudo completar la conexión. Vuelve a intentarlo.",
        accion: "reintentar",
      });
      setWaConnecting(false);
    });
  };

  // 🔴 SINCRONO A PROPOSITO. Ni un `await` entre el clic y FB.login -- ver el comentario de
  // precargarSdk. Si el SDK todavia no esta listo NO se espera: se dice, y el usuario reintenta
  // con la precarga ya terminada.
  const conectarWhatsapp = () => {
    setWaError(null);
    setWaAviso(null);
    signupRef.current = null;

    if (waSdk.estado !== "listo" || !window.FB) {
      setWaError({
        code: "SDK_NO_DISPONIBLE",
        // Aqui aterriza el tope de 10s del helper: el boton que "no hacia nada" ahora dice por que.
        detail: waSdk.error || "El conector de Meta todavía no está listo. Espera un momento y vuelve a intentarlo.",
        accion: "reintentar",
      });
      return;
    }
    setWaConnecting(true);

    // 🔴 EL POST SE DISPARA DESDE ESTE CALLBACK, NUNCA DESDE EL LISTENER DE `message`. El canal (A)
    // llega ANTES, pero no trae el `code`: disparar desde alli mandaria una peticion sin el. Y
    // esperar a tener los dos con un temporizador gastaria TTL -- el code de Meta caduca en 30
    // SEGUNDOS y agency encadena hasta tres llamadas con el. (B) es quien arranca ese reloj, asi
    // que es quien tiene que salir corriendo.
    // 🔴 try/catch alrededor de la llamada: FB.login puede lanzar de forma SINCRONA, ANTES de abrir
    // nada -- es exactamente lo que pasaba al pasarle una async function. Sin este catch, la
    // excepcion sale del handler, el callback no corre nunca y `waConnecting` no se limpia: el
    // boton se queda en "Conectando..." indefinidamente, que es el sintoma que se vio en produccion.
    try {
      // 🔴 `featureType` ACTIVA COEXISTENCE (6 Sep 2026). Sin el, un cliente
      //    que ya usa la app de WhatsApp Business no puede onboardear con SU
      //    numero: el flujo no le ofrece esa opcion.
      //
      // 🔴 LA FORMA NO ESTA INFERIDA: sale del snippet que GENERA el propio
      //    helper de Meta (App Dashboard -> WhatsApp -> Embedded Signup
      //    Builder) al elegir "Registro de app de WhatsApp Business". El valor
      //    literal es `whatsapp_business_app_onboarding` y va al MISMO NIVEL
      //    que los demas, dentro de `extras` -- no anidado, no dentro de
      //    `setup`. ⚠️ El valor `coexistence` de tutoriales viejos YA NO ES
      //    VALIDO segun el changelog de Meta.
      //
      // ⚠️ Y el paso 2 (el listener del `message` event) NO cambia: se
      //    comprobo contra el helper. Una fuente secundaria decia que
      //    `featureType` iba tambien alli, y estaba equivocada.
      //
      // 🔴 `version: "v4"` VA EMPAREJADO CON EL `config_id`, y por eso los dos
      //    cambiaron en el mismo commit (DT.127, 8 Sep 2026). El campo lo emite
      //    el propio helper de Meta para la configuracion v4; declararlo sobre
      //    una configuracion que no lo es seria mentir sobre la version del
      //    flujo. Si algun dia se vuelve a tocar `config_id`, esta clave se
      //    revisa en el mismo movimiento. Meta retira v2, v3 y las dos
      //    public-preview el 15 de octubre de 2026.
      //
      // Las dos decisiones sobre lo que NO se toco, para que nadie las
      // "arregle" mirando el snippet de Meta y viendo que no coinciden:
      //
      //   - SE DEJA `setup: {}` aunque el snippet nuevo no lo traiga: vacio no
      //     hace nada, y "el snippet no lo trae" no es "estorba". (Ese objeto
      //     sirve para prellenar el flujo; `setup.phone.displayName` seria el
      //     cierre natural de DT.100, pero eso es otro cambio.)
      //   - SE DEJA `sessionInfoVersion: "3"`: el evento WA_EMBEDDED_SIGNUP
      //     que se verifico CON TRAFICO REAL en F1-6 llego con el puesto, y
      //     todo el alta depende de la forma de ese payload. Meta lo genera en
      //     la URL hospedada aunque no en el snippet del FB.login.
      window.FB.login(alResponderLogin, {
        config_id: CONFIG_ID,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: "whatsapp_business_app_onboarding",
          sessionInfoVersion: "3",
          version: "v4",
        },
      });
    } catch (e) {
      setWaError({
        code: "SDK_NO_DISPONIBLE",
        detail: "El conector de Meta no pudo abrir la ventana de conexión. Vuelve a intentarlo.",
        accion: "reintentar",
      });
      setWaConnecting(false);
    }
  };

  useEffect(() => {
    if (activeTab !== "escalations" || permisosPA3?.puede_operar === false) return;
    setLoadingEscalations(true);
    api.get("/assistant/escalations")
      .then((r) => setEscalations(r.data.items || []))
      .catch(() => setEscalations([]))
      .finally(() => setLoadingEscalations(false));
  }, [activeTab, permisosPA3?.puede_operar]);

  useImperativeHandle(ref, () => ({ guardar: () => wizardPaso === "whatsapp" ? Promise.resolve(true) : save() }));

  // 🔴 El orden importa: primero «no te corresponde», y sólo después «se rompió algo».
  //    Al revés, un 403 por capacidad volvería a pintarse de rojo como una avería.
  if (sinAcceso)
    return <SinAcceso detail={sinAcceso.detail} capacidad={sinAcceso.capacidad} />;
  if (cfgError) return <div className="p-8 text-rose-500">{cfgError}</div>;
  if (!cfg) return <div className="p-8 text-slate-500">Cargando configuración…</div>;

  const update = (k, v) => setCfg({ ...cfg, [k]: v });

  async function save() {
    if (!cfg || saving || cfgError || sinAcceso) return false;
    if (wizardPaso === "sofi" && (!cfg.assistant_name.trim() || !cfg.welcome_message.trim())) return false;
    if (wizardPaso === "sofi") {
      setSaving(true);
      try {
        await api.put("/assistant/config", { assistant_name: cfg.assistant_name.trim(), welcome_message: cfg.welcome_message.trim() });
        await onSaved?.();
        return true;
      } catch { toast.error("No se pudo guardar"); return false; }
      finally { setSaving(false); }
    }
    if (!phoneDigitsValid(cfg.handoff_phone, false)) {
      toast.error("El teléfono para pasar a humano debe tener 10 dígitos, o déjalo vacío");
      return;
    }
    setSaving(true);
    try {
      await api.put("/assistant/config", {
        assistant_name: cfg.assistant_name,
        welcome_message: cfg.welcome_message,
        business_hours: cfg.business_hours,
        handoff_phone: cfg.handoff_phone,
        emergency_notice: cfg.emergency_notice,
        instrucciones_pago: cfg.instrucciones_pago.trim() || null,
        services_offered: cfg.services_offered || [],
        faqs: cfg.faqs || [],
        tone: cfg.tone,
        auto_replies: cfg.auto_replies,
        human_approval: cfg.human_approval,
        is_active: cfg.is_active,
      });
      toast.success("Configuración guardada");
      await onSaved?.();
      return true;
    } catch { toast.error("No se pudo guardar"); return false; }
    finally { setSaving(false); }
  }

  const loadDoctors = () => api.get("/doctors").then(r => setDoctors(r.data.items || [])).catch(() => {});
  const addFAQ = () => update("faqs", [...(cfg.faqs || []), { q: "", a: "" }]);
  const removeFAQ = (i) => update("faqs", cfg.faqs.filter((_, idx) => idx !== i));
  const updateFAQ = (i, k, v) => update("faqs", cfg.faqs.map((f, idx) => idx === i ? { ...f, [k]: v } : f));



  // CX fase 0-b: el botón de conexión (con su estado de precarga del SDK) vive en UN sitio y se
  // pinta en el ESTADO 3 y, si la clínica tiene el Embedded Signup abierto, también en el ESTADO 2.
  // Mismo JSX y mismo texto que ya existían, más el aviso del historial (22 Oct 2026, aprobado por Jesús).
  const botonConexion = (
    <>
      {/* 🔴 Si la precarga del SDK fallo, NO se ofrece un boton activo: pulsarlo no
          podria abrir nada. Se muestra el motivo y un reintento de la precarga. */}
      {waSdk.estado === "error" ? (
        <div className="text-sm rounded-lg px-3 py-2 bg-rose-50 text-rose-700 space-y-2" data-testid="wa-sdk-error">
          <div>{waSdk.error}</div>
          <button
            onClick={precargarSdk}
            className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border border-rose-300 hover:bg-rose-100"
            data-testid="wa-sdk-reintentar"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Reintentar
          </button>
        </div>
      ) : (
        <button
          onClick={conectarWhatsapp}
          disabled={waConnecting || waSdk.estado !== "listo"}
          className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-[#25D366] text-white hover:bg-[#1ebe5c] transition-colors disabled:opacity-60"
          data-testid="wa-conectar"
        >
          {waConnecting ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Conectando…</>
          ) : waSdk.estado !== "listo" ? (
            /* Deshabilitado con texto propio mientras la precarga corre: un boton
               activo que va a fallar es peor que uno que dice que aun no puede. */
            <><Loader2 className="w-4 h-4 animate-spin" /> Preparando conexión…</>
          ) : (
            <>Conectar WhatsApp</>
          )}
        </button>
      )}
      {/* CX historial: agency guarda en la bandeja el historial que Meta entrega al conectar
          por Coexistence (bandera COEXISTENCE_HISTORIAL). Texto aprobado por Jesús. */}
      <p className="mt-3 text-xs text-slate-500" data-testid="wa-aviso-historial">
        Si conectas el número que ya usas en la app de WhatsApp Business, puedes traer a tu
        bandeja tus chats individuales de los últimos 6 meses (sin grupos). WhatsApp te deja
        elegir en la app si los compartes; si no, la conexión funciona igual. Las fotos, audios
        y documentos anteriores aparecen como aviso, sin el archivo.
      </p>
    </>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-5xl mx-auto w-full">
      <PageHeader
        title={wizardPaso === "whatsapp" ? "WhatsApp" : wizardPaso ? "Sofi" : "Asistente IA — Sofi"}
        subtitle="Configura cómo Sofi conversa con tus pacientes."
        actions={
          (activeTab === "config" || activeTab === "knowledge") ? (
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-brand-electric text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60" data-testid="assistant-save">
              <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar cambios"}
            </button>
          ) : null
        }
      />

      {/* Tab Navigation */}
      {!wizardPaso && <div className="flex border-b border-slate-200 overflow-x-auto">
        {TABS.filter(t => permisosPA3?.puede_operar !== false || ["config", "channels"].includes(t.id)).map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${activeTab === id ? "border-brand-electric text-brand-electric" : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>}
      {/* ── TAB 1 — Configuración ── */}
      {activeTab === "config" && (
        <div className="space-y-5">
          <Card title="Identidad" icon={Bot}>
            {!wizardPaso && <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Sofi activa</span>
              {permisosPA3?.puede_operar !== false && <Toggle value={cfg.is_active} onChange={(v) => update("is_active", v)} testid="toggle-active" />}
            </div>}
            <Field label="Nombre del asistente">
              <input value={cfg.assistant_name} onChange={(e) => update("assistant_name", e.target.value)} className="input" data-testid="assistant-name" />
            </Field>
            <Field label="Mensaje de bienvenida">
              <textarea rows={3} value={cfg.welcome_message} onChange={(e) => update("welcome_message", e.target.value)} className="input" data-testid="assistant-welcome" />
            </Field>
          </Card>

          {!wizardPaso && <>
          <Card title="Instrucciones de pago del anticipo">
            <Field label="Instrucciones de pago del anticipo">
              <textarea rows={5} maxLength={1000} value={cfg.instrucciones_pago} onChange={(e) => update("instrucciones_pago", e.target.value)} className="input" data-testid="assistant-instrucciones-pago" aria-describedby="instrucciones-pago-ayuda" />
            </Field>
            <p id="instrucciones-pago-ayuda" className="text-sm text-slate-500">Sofi enviará este texto tal cual al paciente cuando agende un servicio con anticipo o pregunte cómo pagar. Incluye banco, titular y CLABE, o tu enlace de pago.</p>
          </Card>
          <Card title="Tono de la IA">
            <div className="grid grid-cols-3 gap-3">
              {TONES.map((t) => (
                <button key={t.id} onClick={() => update("tone", t.id)}
                  className={`text-left rounded-xl border p-4 transition-colors ${cfg.tone === t.id ? "border-brand-electric bg-blue-50/60" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid={`tone-${t.id}`}
                >
                  <div className="font-semibold text-slate-900">{t.label}</div>
                  <div className="text-xs text-slate-500 mt-1 leading-snug">{t.desc}</div>
                </button>
              ))}
            </div>
          </Card>

          {/*
            J1 PASO 1 (DT.61, cerrada): el campo System Prompt se eliminó de este panel a
            propósito, no se ocultó tras una bandera. El system_prompt sigue existiendo en la
            columna assistant_config.system_prompt, se sigue leyendo y se sigue enviando a
            agency como capa 1 (GET /internal/clinic/{id}/assistant-config) -- lo único que se
            quitó es la capacidad del CLIENTE de editarlo desde aquí.

            Motivo: un system_prompt del cliente puede contradecir las reglas duras del motor
            (capa 2, HARD_SAFETY_RULES + _CLINICAS_FLOW_RULES en agency). Ya pasó en producción
            con clinic-altura -- el prompt decía "No confirmas ni agendas citas por tu cuenta"
            mientras el código le ordenaba lo contrario a Sofi en la misma conversación, y costó
            ocho intentos de diagnóstico encontrar la causa real (ver DT.61 / bloque S9-D-H en
            el historial de este archivo). Reescribir el prompt vía SQL directo, sin pasar por
            un cliente sin ese contexto, es la única vía hoy -- no hay validación de contenido
            que pueda prevenir esa clase de contradicción de forma confiable.
          */}

          <Card title="Contacto y horarios" icon={Clock}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Horarios de atención" icon={Clock}>
                <input value={cfg.business_hours} onChange={(e) => update("business_hours", e.target.value)} className="input" data-testid="assistant-hours" />
              </Field>
              <Field label="Teléfono para pasar a humano" icon={Phone}>
                <PhoneInput value={cfg.handoff_phone} onChange={(digits) => update("handoff_phone", digits)} testId="assistant-handoff" />
              </Field>
            </div>
            <Field label="Aviso para emergencias" icon={AlertOctagon}>
              <textarea rows={2} value={cfg.emergency_notice} onChange={(e) => update("emergency_notice", e.target.value)} className="input" data-testid="assistant-emergency" />
            </Field>
          </Card>

          <Card title="Automatización">
            {permisosPA3?.puede_operar !== false && <Toggle label="Respuestas automáticas activadas" value={cfg.auto_replies} onChange={(v) => update("auto_replies", v)} testid="toggle-auto" />}
            <Toggle label="Modo aprobación humana" value={cfg.human_approval} onChange={(v) => update("human_approval", v)} testid="toggle-approval" />
            <p className="text-xs text-slate-500">Con respuestas automáticas desactivadas, aprobación humana activada o Sofi inactiva, los mensajes quedan para atención manual en la bandeja. Sofi no responde ni agenda; las urgencias siguen atendidas por seguridad.</p>
            <div className="text-xs text-slate-500 mt-2 leading-relaxed">En modo aprobación humana, la IA prepara la respuesta pero un humano debe enviarla.</div>
          </Card>

          {/* Doctores */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <span className="font-display font-bold inline-flex items-center gap-2">
                <UserRound className="w-4 h-4 text-brand-electric" /> Doctores
              </span>
              <button onClick={() => { setEditDoc(null); setShowDocModal(true); }} className="inline-flex items-center gap-1 text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 px-2.5 py-1.5 rounded-lg" data-testid="add-doctor-btn">
                <Plus className="w-3.5 h-3.5" /> Agregar doctor
              </button>
            </div>
            <div className="p-5">
              {/* 🔴 «No hay doctores registrados» es una AFIRMACIÓN sobre la clínica, y
                  con el `catch` vacío de antes se pintaba también cuando el catálogo había
                  devuelto 403 -- diciéndole a alguien sin `agenda` que su clínica no tiene
                  médicos. Un cero por falta de permiso y un cero por falta de datos se leen
                  igual, y es la tercera vez que este repo tropieza con eso (B3-a, DT.233). */}
              {doctoresError ? (
                <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                     data-testid="doctores-sin-acceso">{doctoresError}</div>
              ) : doctors.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-6">No hay doctores registrados.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {doctors.map(doc => (
                    <DoctorCard key={doc.id} doc={doc}
                      onEdit={() => { setEditDoc(doc); setShowDocModal(true); }}
                      onDelete={async () => {
                        if (!window.confirm(`¿Eliminar al Dr. ${doc.name}?`)) return;
                        await api.delete(`/doctors/${doc.id}`);
                        await loadDoctors();
                        toast.success("Doctor eliminado");
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          </>}
        </div>
      )}

      {/* ── TAB 2 — Conocimiento ── */}
      {activeTab === "knowledge" && (
        <div className="space-y-5">
          <Card title="Preguntas frecuentes (FAQs)" icon={BookOpen}>
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs text-slate-500">Sofi usará estas respuestas para contestar preguntas comunes.</div>
              <button onClick={addFAQ} className="text-xs font-medium inline-flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 px-2.5 py-1.5 rounded-lg" data-testid="faq-add">
                <Plus className="w-3.5 h-3.5" /> Agregar FAQ
              </button>
            </div>
            <div className="space-y-3">
              {(cfg.faqs || []).length === 0 && (
                <div className="text-sm text-slate-500 text-center py-8 border-2 border-dashed border-slate-200 rounded-xl">
                  No hay FAQs. Agrega preguntas frecuentes de tus pacientes.
                </div>
              )}
              {(cfg.faqs || []).map((f, i) => (
                <div key={i} className="border border-slate-200 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 space-y-2">
                      <input value={f.q} onChange={(e) => updateFAQ(i, "q", e.target.value)} placeholder="Pregunta del paciente…" className="input" data-testid={`faq-q-${i}`} />
                      <textarea rows={2} value={f.a} onChange={(e) => updateFAQ(i, "a", e.target.value)} placeholder="Respuesta de Sofi…" className="input" data-testid={`faq-a-${i}`} />
                    </div>
                    <button onClick={() => removeFAQ(i)} className="p-2 text-slate-400 hover:text-rose-600 mt-0.5" data-testid={`faq-remove-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Documentos de referencia">
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center text-sm text-slate-500 flex flex-col items-center gap-2">
              <BookOpen className="w-6 h-6 text-slate-400" />
              Sube documentos que Sofi puede consultar
              <button className="text-brand-electric font-medium" onClick={() => toast.info("Próximamente — carga de documentos PDF")} data-testid="kb-upload">
                Agregar documento
              </button>
              <div className="text-[11px] text-slate-400">Aceptamos políticas, lista de precios, tratamientos, etc.</div>
            </div>
          </Card>
        </div>
      )}

      {/* ── TAB 4 — Canales ── */}
      {activeTab === "channels" && (
        <div className="space-y-4">
          {/* 🔴 TEN.4 -- aquí había una tarjeta "Chat Web — Activo" con un código para embeber y el
              texto "el widget incluye chat con Sofi y la opción de agendar citas". MEDIDO: el widget
              (`surit.ai/assistant-widget.js`) es de RESERVAS y no tiene chat; llama a `api.surit.ai`,
              que NO EXISTE (NXDOMAIN); con el id de clínica como tenant agency responde 404; y en 30
              días no pasó ni una petición. Un canal marcado "Activo" que no existe. Se RETIRA de
              todas las clínicas; vuelve cuando el chat web exista y funcione de punta a punta
              (pendiente de producto en CLAUDE.md). */}
          {/*
            WhatsApp — Embedded Signup (F1-4a-2, 25 Ago 2026). Sustituye al texto incondicional que
            dejo J1: entonces este repo no tenia forma de saber si la clinica estaba conectada, y
            "un estado que no se puede saber no se muestra". Desde F1-4b (agency) y F1-4a-1 (el
            proxy GET /api/whatsapp/status) SI se puede, asi que ahora se muestra.

            🔴 SON CUATRO RAMAS, NO DOS. `connected` no es un booleano: distingue tres estados por
            si mismo, y encima la lectura puede fallar. Nunca `if (!connected) mostrarBoton` -- eso
            funde "la fila existe desactivada" con "nunca hubo conexion", que se arreglan de forma
            distinta (reactivar vs. conectar). Ver DT.93 y la seccion "Estado de la conexion de
            WhatsApp" del CLAUDE.md.
          */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              <span className="font-display font-bold">WhatsApp</span>
              {waStatus?.connected && (
                <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">Conectado</span>
              )}
            </div>
            <div className="p-5 space-y-3">
              {esperaPA3 ? <div role="status"><p className="font-semibold">{COPY_PA3.espera}</p><p>{COPY_PA3.esperaDetalle}</p></div> : waLoading ? (
                <div className="text-sm text-slate-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Consultando el estado…
                </div>

              /* ── ESTADO 4 — no se pudo leer el estado. SIN boton de conectar, a proposito:
                    ofrecer conectar sin saber si ya lo esta puede llevar a que el usuario intente
                    dar de alta un numero que ya existe y se lleve un PHONE_NUMBER_ALREADY_CONNECTED
                    sin entender por que. Aqui aterriza tambien la sesion demo (el backend da 403). ── */
              ) : waStatusError ? (
                <>
                  <div className="text-sm text-slate-700 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span>{waStatusError.detail}</span>
                  </div>
                  {waStatusError.code !== "DEMO" && (
                    <button
                      onClick={cargarWhatsappStatus}
                      className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                      data-testid="wa-status-reintentar"
                    >
                      <RefreshCw className="w-4 h-4" /> Reintentar
                    </button>
                  )}
                </>

              /* ── ESTADO 1 — conectada y activa ── */
              ) : waStatus?.connected ? (
                <>
                  <div className="text-sm text-slate-700 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    {/* 🔴 display_phone_number PUEDE SER NULL aunque la conexion este activa: en
                        clinic-pruebas, la unica clinica conectada hoy, lo es (DT.100 de agency).
                        Un tab que asuma que hay numero legible enseña un hueco justo en el unico
                        caso real que existe. phone_number_id si esta siempre que haya fila. */}
                    <span>
                      Tu WhatsApp está conectado
                      {waStatus.display_phone_number
                        ? <>: <strong>{waStatus.display_phone_number}</strong></>
                        : waStatus.phone_number_id
                          ? <> (id <span className="font-mono text-xs">{waStatus.phone_number_id}</span>)</>
                          : null}
                    </span>
                  </div>
                  {waStatus.has_own_token === false && (
                    /* Nota discreta, NO una alarma: hoy es el estado normal de todas las clinicas
                       (token compartido de la app de Surit mientras Embedded Signup propio siga
                       bloqueado del lado de Meta). Decirlo como advertencia asustaria sin motivo. */
                    <div className="text-xs text-slate-400">Número administrado por Surit.</div>
                  )}
                </>

              /* ── ESTADO 2 — hay fila, pero desactivada.
                    🔴 RAMA NO EJERCITADA CONTRA DATOS REALES: al 25 Ago 2026 ninguna clinica esta
                    en este estado (barrido de las 7 cuentas con clinica en F1-4a-1), y provocarlo
                    exigiria desactivar la unica conexion viva. Verificada solo con datos simulados.
                    CX fase 0-b: con el Embedded Signup ABIERTO para esta clínica se ofrece ADEMÁS el
                    botón de conexión (el mismo del ESTADO 3). Es el camino de Coexistence: el número
                    se deregistra de Cloud API, la fila queda desactivada y se reconecta desde aquí.
                    Ya no choca con PHONE_NUMBER_ALREADY_CONNECTED: desde TEN.3 el alta resuelve el
                    tenant de la clínica y la fila es suya (agency 46b52b3). Sin la bandera, sólo
                    soporte, como antes. ── */
              ) : waStatus?.phone_number_id ? (
                <>
                  <div className="text-sm text-slate-700 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span>
                      Tu número está registrado pero la conexión está desactivada, así que Sofi no
                      recibe mensajes. Podemos reactivarla desde soporte.
                    </span>
                  </div>
                  {correoSoporte && (
                  <a
                    href={enlaceSoporte(correoSoporte, `Hola, mi WhatsApp aparece desactivado en el panel (clínica: ${clinicSlug}).`)}
                    className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid="wa-soporte-desactivada"
                  >
                    <Mail className="w-4 h-4" /> Contactar soporte por correo
                  </a>
                  )}
                  {signupAbierto && botonConexion}
                </>

              /* ── ESTADO 3 — nunca hubo conexion. TEN.4: conexion ACOMPAÑADA, o el plan no la incluye. ── */
              ) : !signupAbierto ? (
                <WhatsappSinConexion planWa={planWa} />
              ) : (
                <>
                  <div className="text-sm text-slate-600">
                    Conecta el WhatsApp de tu clínica para que Sofi atienda a tus pacientes ahí.
                    Meta te pedirá iniciar sesión y elegir el número.
                  </div>
                  {botonConexion}
                </>
              )}

              {/* Desenlaces del canal (A) que no son un alta: abandono o error reportado por Meta */}
              {waAviso && (
                <div className={`text-sm rounded-lg px-3 py-2 ${waAviso.tono === "error" ? "bg-rose-50 text-rose-700" : "bg-slate-50 text-slate-600"}`}>
                  {waAviso.texto}
                  {waAviso.pie && <div className="text-xs text-slate-400 mt-1">{waAviso.pie}</div>}
                </div>
              )}

              {/* Error del POST /whatsapp/connect: mensaje del backend + la accion que toca */}
              {waError && (
                <div className="text-sm rounded-lg px-3 py-2 bg-rose-50 text-rose-700 space-y-2" data-testid="wa-connect-error">
                  <div>{waError.detail}</div>
                  {waError.extra && <div className="text-xs text-rose-600/80">{waError.extra}</div>}
                  {waError.accion === "reintentar" && (
                    <button onClick={conectarWhatsapp} disabled={waConnecting}
                      className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border border-rose-300 hover:bg-rose-100 disabled:opacity-60">
                      <RefreshCw className="w-3.5 h-3.5" /> Reintentar
                    </button>
                  )}
                  {waError.accion === "soporte" && correoSoporte && (
                    <a href={enlaceSoporte(correoSoporte, `Hola, tuve un problema conectando WhatsApp (clínica: ${clinicSlug}, código: ${waError.code}).`)}
                      className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border border-rose-300 hover:bg-rose-100"
                      data-testid="wa-soporte-error">
                      <Mail className="w-3.5 h-3.5" /> Contactar soporte por correo
                    </a>
                  )}
                  {waError.accion === "sesion" && (
                    <div className="text-xs text-rose-600/80">Vuelve a iniciar sesión y repite la conexión.</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Voz */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden opacity-60">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
              <Phone className="w-5 h-5 text-slate-400" />
              <span className="font-display font-bold">Voz</span>
              <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">Próximamente</span>
            </div>
            <div className="p-5">
              <div className="text-sm text-slate-500">Sofi atenderá llamadas telefónicas, responderá preguntas y agendará citas por voz.</div>
              <button disabled className="mt-4 text-sm px-4 py-2 rounded-lg border border-slate-200 text-slate-400 cursor-not-allowed">Configurar Voz</button>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 5 — Urgencias ── */}
      {activeTab === "escalations" && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <span className="font-display font-bold">Escalamientos por posible urgencia</span>
            <div className="text-xs text-slate-500 mt-1">
              Registro de solo lectura. Cuando Sofi detecta una posible urgencia en WhatsApp, responde al paciente con los teléfonos de emergencia (911 y, si es una crisis de salud mental, la Línea de la Vida) o te pasa la conversación. El mensaje queda guardado aquí. Este registro no se puede editar ni borrar.
            </div>
          </div>
          <div className="p-5">
            {loadingEscalations ? (
              <div className="text-sm text-slate-500 text-center py-8">Cargando…</div>
            ) : escalations.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-8 border-2 border-dashed border-slate-200 rounded-xl">
                Sin escalamientos registrados.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200">
                      <th className="text-left py-2 pr-4 font-semibold">Fecha</th>
                      <th className="text-left py-2 pr-4 font-semibold">Identificador</th>
                      <th className="text-left py-2 pr-4 font-semibold">Mensaje</th>
                      <th className="text-left py-2 font-semibold">Canal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {escalations.map((e) => (
                      <tr key={e.id} className="border-b border-slate-100">
                        <td className="py-3 pr-4 text-slate-500 whitespace-nowrap">{new Date(e.created_at).toLocaleString("es-MX")}</td>
                        <td className="py-3 pr-4 text-slate-600 font-mono text-xs">{e.patient_identifier || e.session_id}</td>
                        <td className="py-3 pr-4 text-slate-900">{e.message}</td>
                        <td className="py-3 text-slate-500">{e.channel}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {showDocModal && (
        <DoctorModal doc={editDoc} onClose={() => setShowDocModal(false)} onSaved={async () => { await loadDoctors(); setShowDocModal(false); }} />
      )}

      <style>{`
        .input { width:100%; border:1px solid #cbd5e1; background:#fff; border-radius:8px; padding:.55rem .75rem; font-size:.875rem; outline:none; }
        .input:focus { border-color:#2563EB; box-shadow:0 0 0 2px rgba(37,99,235,.18); }
        select.input { cursor:pointer; }
      `}</style>
    </div>
  );
});
export default AssistantConfig;

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 font-display font-bold inline-flex items-center gap-2 w-full">
        {Icon && <Icon className="w-4 h-4 text-brand-electric" />} {title}
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Toggle({ label, value, onChange, testid }) {
  return (
    <button onClick={() => onChange(!value)} className="w-full flex items-center justify-between py-2 group" data-testid={testid}>
      {label && <span className="text-sm text-slate-700">{label}</span>}
      <span className={`relative w-10 h-6 rounded-full transition-colors shrink-0 ${value ? "bg-emerald-500" : "bg-slate-300"}`}>
        <span className={`absolute top-0.5 ${value ? "left-5" : "left-0.5"} w-5 h-5 bg-white rounded-full shadow transition-all`} />
      </span>
    </button>
  );
}

function DoctorCard({ doc, onEdit, onDelete }) {
  const initial = (doc.name || "D")[0].toUpperCase();
  return (
    <div className="border border-slate-200 rounded-xl p-4 flex items-start gap-3">
      <div className="w-10 h-10 rounded-full grid place-items-center text-white font-bold text-sm shrink-0" style={{ backgroundColor: doc.color || "#2563EB" }}>
        {initial}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900 text-sm truncate">{doc.name}</div>
        <div className="text-xs text-slate-500 truncate">{doc.specialty || "Sin especialidad"}</div>
        <div className="text-xs text-slate-400 truncate">{formatAvailability(doc.availability)}</div>
      </div>
      <div className="flex gap-1 shrink-0">
        <button onClick={onEdit} className="p-1.5 text-slate-400 hover:text-blue-600 rounded-lg hover:bg-blue-50" title="Editar"><Edit2 className="w-3.5 h-3.5" /></button>
        <button onClick={onDelete} className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-rose-50" title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

function DoctorModal({ doc, onClose, onSaved }) {
  const isEdit = !!doc;
  const [form, setForm] = useState({
    name: doc?.name || "",
    specialty: doc?.specialty || "",
    color: doc?.color || "#2563EB",
  });
  const [saving, setSaving] = useState(false);
  // Horario (doctor_availability, S5b) -- arreglo plano de {day_of_week, start_time, end_time}
  const [availability, setAvailability] = useState([]);

  useEffect(() => {
    if (!doc?.id) return;
    api.get(`/doctors/${doc.id}/availability`)
      .then(({ data }) => setAvailability(
        (data.items || []).map(({ day_of_week, start_time, end_time }) => ({ day_of_week, start_time, end_time }))
      ))
      .catch(() => setAvailability([]));
  }, [doc?.id]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("El nombre es requerido"); return; }
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        const { data } = await api.patch(`/doctors/${doc.id}`, form);
        saved = data;
      } else {
        const { data } = await api.post("/doctors", form);
        saved = data;
      }
      if (saved?.id) {
        try {
          await api.put(`/doctors/${saved.id}/availability`, { slots: availability });
        } catch {
          toast.error("Doctor guardado, pero no se pudo guardar el horario");
        }
      }
      toast.success(isEdit ? "Doctor actualizado" : "Doctor creado");
      onSaved();
    } catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <span className="font-display font-bold">{isEdit ? "Editar doctor" : "Nuevo doctor"}</span>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Nombre</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="input mt-1" placeholder="Dr. María García" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Especialidad</label>
            <input value={form.specialty} onChange={e => setForm(f => ({ ...f, specialty: e.target.value }))} className="input mt-1" placeholder="Medicina general" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 block mb-2">Color</label>
            <div className="flex gap-2">
              {DOC_COLORS.map(c => (
                <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))} className="w-8 h-8 rounded-full transition-all" style={{ backgroundColor: c, outline: form.color === c ? `3px solid ${c}` : "none", outlineOffset: 2 }} />
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 block mb-2">Horario</label>
            <DoctorAvailabilityEditor value={availability} onChange={setAvailability} />
            {isEdit && doc?.id && <AusenciasDoctor key={doc.id} doctorId={doc.id} sustantivo="doctor" />}
          </div>
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="text-sm px-4 py-2 rounded-lg bg-brand-electric text-white hover:bg-blue-700 disabled:opacity-60">
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
      <style>{`.input { width:100%; border:1px solid #cbd5e1; background:#fff; border-radius:8px; padding:.55rem .75rem; font-size:.875rem; outline:none; } .input:focus { border-color:#2563EB; box-shadow:0 0 0 2px rgba(37,99,235,.18); }`}</style>
    </div>
  );
}


// TEN.4 -- lo que ve una clínica SIN WhatsApp conectado. Nada que prometa conectar solo.
// ⚠️ COPY PENDIENTE DE APROBACIÓN (parada 3.5 de TEN.4).
function WhatsappSinConexion({ planWa }) {
  // El WhatsApp para agendar es UNA constante del backend (TEN.4): una PERSONA, no el número
  // comercial de platform, que lo contesta el asistente.
  const alta = useDatosAlta();
  if (planWa.estado === "cargando") {
    return <div className="text-sm text-slate-500">Consultando tu plan…</div>;
  }
  if (planWa.estado === "ok" && planWa.sofi === false) {
    return (
      <div className="text-sm text-slate-600 space-y-2" data-testid="wa-plan-sin-whatsapp">
        <div>Tu plan no incluye WhatsApp ni a Sofi.</div>
      </div>
    );
  }
  return (
    <div className="text-sm text-slate-600 space-y-3" data-testid="wa-acompanada">
      <div>
        La conexión del WhatsApp de tu clínica la hacemos contigo. Escríbenos para agendarla: te guiamos
        paso a paso con tu número.
      </div>
      {alta.datos?.conexion_whatsapp_url ? (
        <a
          href={alta.datos.conexion_whatsapp_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-[#25D366] text-white hover:bg-[#1ebe5c]"
          data-testid="wa-agendar-conexion"
        >
          Agendar la conexión
        </a>
      ) : alta.estado === "error" ? (
        <div className="text-xs text-amber-700">
          No pudimos cargar el contacto para agendarla.{" "}
          <button onClick={alta.reintentar} className="underline">Reintentar</button>
        </div>
      ) : null}
    </div>
  );
}
